import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowDown } from "lucide-react";
import FloatingLeaves from "./FloatingLeaves";

const HERO_LINES = ["Rooted in Nature.", "Crafted for Wellness."];

export default function KineticHero() {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 800], [0, 160]);
  const y2 = useTransform(scrollY, [0, 800], [0, -80]);
  const y3 = useTransform(scrollY, [0, 800], [0, 240]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0.3]);

  return (
    <section
      ref={ref}
      data-testid="hero"
      className="relative min-h-[100svh] w-full overflow-hidden grain bg-[color:var(--sr-bg)]"
    >
      {/* Sculpted background wash */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-24 w-[70vw] h-[70vw] rounded-full bg-[color:var(--sr-secondary)] blur-3xl opacity-70" />
        <div className="absolute -bottom-40 right-0 w-[55vw] h-[55vw] rounded-full bg-[color:var(--sr-accent)]/10 blur-3xl" />
      </div>

      <FloatingLeaves count={9} tint="rgba(33,75,56,0.09)" />

      <motion.div
        style={{ opacity: heroOpacity }}
        className="sr-container relative z-10 pt-32 md:pt-40 pb-24"
      >
        <div className="grid grid-cols-12 gap-6 md:gap-10 items-end min-h-[80vh]">
          {/* Left copy */}
          <div className="col-span-12 md:col-span-6 lg:col-span-6 flex flex-col">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15 }}
              data-testid="hero-eyebrow"
              className="label-eyebrow text-[color:var(--sr-primary)] mb-6 md:mb-10 flex items-center gap-3"
            >
              <span className="w-8 h-px bg-[color:var(--sr-accent)]" />
              A slow-crafted collection · Est. Small Batch
            </motion.p>

            <h1
              className="font-serif text-[11vw] md:text-[7.4vw] lg:text-[6.2vw] leading-[0.95] tracking-tight text-[color:var(--sr-primary)]"
              data-testid="hero-headline"
            >
              {HERO_LINES.map((line, idx) => (
                <span className="mask-hero-line" key={idx}>
                  <motion.span
                    initial={{ y: "110%" }}
                    animate={{ y: "0%" }}
                    transition={{
                      duration: 1.15,
                      ease: [0.22, 1, 0.36, 1],
                      delay: 0.35 + idx * 0.22,
                    }}
                    className="inline-block will-change-transform"
                  >
                    {idx === 0 ? (
                      line
                    ) : (
                      <>
                        Crafted for{" "}
                        <em className="italic font-light text-[color:var(--sr-accent)]">
                          Wellness.
                        </em>
                      </>
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.05 }}
              className="max-w-md md:max-w-lg text-[color:var(--sr-text)]/75 mt-8 md:mt-12 text-base md:text-lg leading-relaxed"
            >
              Discover handcrafted beverages designed to inspire balance,
              comfort, and everyday moments of enjoyment.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 1.2 }}
              className="mt-10 md:mt-12 flex flex-col sm:flex-row items-start gap-4 sm:gap-6"
            >
              <Link
                to="/shop"
                data-testid="hero-cta-shop"
                className="group inline-flex items-center gap-3 px-8 md:px-10 py-4 md:py-5 bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow hover:bg-[color:var(--sr-primary-deep)] transition-colors"
              >
                <span>Shop the Collection</span>
                <span className="w-6 h-px bg-current transition-all group-hover:w-10" />
              </Link>
              <Link
                to="/about"
                data-testid="hero-cta-story"
                className="group inline-flex items-center gap-3 py-4 md:py-5 label-eyebrow text-[color:var(--sr-primary)] hover:opacity-70 transition-opacity"
              >
                <span>Our Story</span>
                <span className="w-6 h-px bg-current transition-all group-hover:w-10" />
              </Link>
            </motion.div>
          </div>

          {/* Right — 5-bottle lineup with 3D parallax + halo */}
          <div className="col-span-12 md:col-span-6 relative h-[52vh] md:h-[80vh]">
            <motion.div
              style={{ y: y1 }}
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0, y: 60, scale: 1.04 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1.6, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
              data-testid="hero-bottles"
            >
              <img
                src="/hero-bottles-3d.png"
                alt="The Smiling Roots — Golden Nectar, Dark Fudge, Vanilla Cream, Pineapple Paradise, Hibiscus Bloom"
                className="w-full h-full object-contain drop-shadow-[0_40px_60px_rgba(33,75,56,0.22)]"
                draggable="false"
              />
            </motion.div>
            {/* Soft gold halo behind bottles */}
            <motion.div
              style={{ y: y2 }}
              className="absolute inset-0 -z-10 flex items-center justify-center pointer-events-none"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.6, delay: 0.4 }}
            >
              <div className="w-[70%] h-[55%] rounded-full bg-[color:var(--sr-accent)]/15 blur-3xl" />
            </motion.div>
            <motion.div
              className="absolute -bottom-2 left-0 label-eyebrow text-[color:var(--sr-primary)]/60"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5 }}
            >
              N°01 — 05
            </motion.div>
          </div>
        </div>

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.7, duration: 0.8 }}
          className="absolute bottom-6 md:bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-[color:var(--sr-primary)]/70"
        >
          <span className="label-eyebrow">Scroll</span>
          <motion.span
            animate={{ y: [0, 6, 0] }}
            transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          >
            <ArrowDown size={16} strokeWidth={1.25} />
          </motion.span>
        </motion.div>
      </motion.div>
    </section>
  );
}
